// BeingMadeLine — while the assistant works on a request, a quiet line where
// the result will land: "Being made: <the Author's words>" (principle 2: the
// first visible change follows at once). The working mark sits in the
// margin, like the sparkle of a line that asks (AskLine.tsx, pen Q66); the
// words are the Author's own, so it claims nothing beyond "you asked, and
// work is running". It goes when the first real part lands or the turn ends
// (`beingMadeOf`, chat/beingMade.ts). A design gap pen has not drawn yet:
// it reuses the asking line's look.
//
// While the assistant writes a course's plan, the outline written so far
// shows under the line: the course title, then one row per part, dimmed,
// under "Being made". They are text, not links: nothing exists to open until
// the course lands, and then the real outline replaces them.
//
// Pure: the words come in; nothing goes out.

export { BeingMadeLine };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Mark } from "../Mark/Mark.js";
import "../Editor/AskLine/AskLine.css";

const BeingMadeLine = ({
    words,
    outline,
    className,
}: {
    readonly words: string;
    readonly outline?:
        | { readonly title?: string; readonly parts: readonly string[] }
        | undefined;
    // The page's reading type (the paragraph's own class).
    readonly className?: string;
}): ReactNode => {
    const { t } = useLingui();
    const line = (
        <p
            className={`bilda-ask-line bilda-being-made${className ? ` ${className}` : ""}`}
            role="status"
            title={words}
        >
            <Mark
                state={{ type: "thinking" }}
                size="sm"
                className="bilda-ask-line__mark"
            />
            <span className="bilda-being-made__text">
                {t`Being made: ${words}`}
            </span>
        </p>
    );
    if (outline === undefined) return line;
    // One element: the line sits in a grid cell among a page's lines.
    return (
        <div className="bilda-being-made__group">
            {line}
            <ul
                className="bilda-being-made__outline"
                aria-label={t`Outline being made`}
            >
                {outline.title === undefined ? null : (
                    <li className="bilda-being-made__course">
                        {outline.title}
                    </li>
                )}
                {outline.parts.map((part, i) => (
                    // Titles may repeat; the position is the row's identity.
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows only grow at the end
                    <li key={i} className="bilda-being-made__part">
                        {part}
                    </li>
                ))}
            </ul>
        </div>
    );
};
